/**
 * CardDecksMainPageV2 — Card Decks list, ported from the prototype's
 * `.screen-carddecks` (cms-redesign-mockup.html ~6638+, deck-card
 * styles 1405–1539).
 *
 * Layout:
 *   • PageHeader (Content Library · Card Decks N items + sub copy +
 *     coral "+ New Deck").
 *   • Status SegmentedControl (All / Active / Inactive with counts).
 *   • `.deck-grid` — repeat(auto-fill, minmax(340px, 1fr)) of rich
 *     deck cards. Each card has:
 *       – 4px top edge tinted by primary_color_hex
 *       – 16:9 cover with module_image_url as background and
 *         header_image_url overlaid as foreground (50% × 50% centered,
 *         object-fit: contain — mirrors the in-app composite)
 *       – Drag handle pill in the cover (top-right, white frosted bg)
 *       – Status pin (top-left)
 *       – Body: deck name + module_description italic tagline +
 *         description (clamped) + stats line ("52 cards · 3 free
 *         preview" + Free/Premium tier pill)
 *       – Footer with two buttons: "Manage Cards →" → /:id/cards,
 *         "Edit" → /:id
 *
 * Drag-to-reorder uses @dnd-kit; drop dispatches `sortCardDeck` (PUT
 * /decks/sort-order) with the same payload v1 already uses.
 *
 * Data: reuses `fetchAllDecks`. The list endpoint already exposes
 * primary_color_hex, module_image_url, header_image_url,
 * module_description, description, active_card_count, card_count,
 * is_active, is_premium, free_view_limit, sort_order.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchAllDecks,
  sortCardDeck,
} from '../../../../actions/cardDeckActions';
import {
  PageHeader,
  Button,
  SegmentedControl,
  TierPill,
  cn,
} from '../../../redesign';

/* ─── Icons ────────────────────────────────────────────────────────── */

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

const DragHandleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="9"  cy="6"  r="0.5" />
    <circle cx="9"  cy="12" r="0.5" />
    <circle cx="9"  cy="18" r="0.5" />
    <circle cx="15" cy="6"  r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

/* ─── Sortable deck card ──────────────────────────────────────────── */

function SortableDeckCard({ deck }) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: deck.id });

  const active = !!deck.is_active;
  const free = !deck.is_premium;
  // Brand color drives the 4px top accent edge — falls back to teal.
  const deckColor = deck.primary_color_hex
    ? (deck.primary_color_hex.startsWith('#') ? deck.primary_color_hex : `#${deck.primary_color_hex}`)
    : '#75A293';

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 5 : undefined,
  };

  const activeCardCount = Number(deck.active_card_count ?? 0);
  const freeViewLimit = Number(deck.free_view_limit ?? 0);

  return (
    <div
      ref={setNodeRef}
      style={dragStyle}
      {...attributes}
      className={cn(
        'flex flex-col bg-surface border border-sand overflow-hidden',
        'transition-all duration-200 relative',
        'hover:border-teal-green',
      )}
      // The wrapper holds dnd transform; the inner contents handle hover.
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = (e.currentTarget.style.transform || '') + ' translateY(-2px)';
        e.currentTarget.style.boxShadow =
          '0 10px 24px #2C3E5018, 0 2px 6px #2C3E500C';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = CSS.Transform.toString(transform) || '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* 4px brand color accent edge — sits above other content. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 4,
          background: deckColor,
          zIndex: 2,
        }}
      />

      {/* Cover — 16:9. module_image is the background scene;
       *  header_image floats centered at 50%×50%. Matches the in-app
       *  composite recipe (prototype 1446–1455). */}
      <div
        className="bg-cream"
        style={{
          aspectRatio: '16 / 9',
          backgroundImage: deck.module_image_url
            ? `url('${deck.module_image_url}')`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          overflow: 'hidden',
          filter: active ? undefined : 'grayscale(40%) brightness(0.95)',
        }}
      >
        {deck.header_image_url && (
          <img
            src={deck.header_image_url}
            alt=""
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '50%',
              height: '50%',
              objectFit: 'contain',
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Status pin — top-left, teal=active or muted=inactive. */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 14,
            left: 14,
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: active ? '#75A293' : '#9CA1A8',
            boxShadow: '0 0 0 3px #FFFFFFCC',
          }}
        />

        {/* Drag handle — top-right white frosted pill. listeners only
         *  bound here so clicking elsewhere on the card doesn't drag. */}
        <span
          {...listeners}
          aria-label="Drag to reorder"
          tabIndex={0}
          className={cn(
            'grid place-items-center text-navy cursor-grab active:cursor-grabbing',
            'focus:outline-none',
          )}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 28,
            height: 28,
            background: '#FFFFFFCC',
            backdropFilter: 'blur(4px)',
            borderRadius: 8,
            transition: 'all 140ms ease',
            boxShadow: '0 2px 6px #00000028',
            touchAction: 'none',
          }}
        >
          <span style={{ width: 14, height: 14, display: 'inline-flex' }}>
            {DragHandleIcon}
          </span>
        </span>

        {!active && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: '#FAF4EA66',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>

      {/* Body — name, tagline, description, stats. */}
      <div
        className="flex flex-col"
        style={{ padding: '14px 16px 12px', gap: 4, flex: 1 }}
      >
        <div
          className={cn(
            'font-bold text-navy',
            !active && 'italic text-text-light',
          )}
          style={{ fontSize: 16, letterSpacing: '-0.005em', lineHeight: 1.25 }}
        >
          {deck.name || 'Untitled deck'}
        </div>
        {deck.module_description && (
          <div
            className="text-text-light italic truncate"
            style={{ fontSize: 12, marginTop: 2 }}
            title={deck.module_description}
          >
            {deck.module_description}
          </div>
        )}
        {deck.description && (
          <div
            className="text-text-light"
            style={{
              marginTop: 4,
              fontSize: 12.5,
              lineHeight: 1.45,
              minHeight: 36,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {deck.description}
          </div>
        )}
        <div
          className="flex items-center"
          style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}
        >
          {/* `.deck-count` — JetBrains Mono pill with the card stats. */}
          <span
            className="font-mono text-text bg-cream border border-sand inline-flex items-center"
            style={{
              fontSize: 11.5,
              padding: '3px 8px',
              borderRadius: 999,
            }}
          >
            <strong className="text-navy font-bold">{activeCardCount}</strong>
            <span style={{ margin: '0 4px' }}>cards</span>
            {freeViewLimit > 0 && (
              <>
                <span aria-hidden style={{ margin: '0 4px', opacity: 0.5 }}>·</span>
                <strong className="text-navy font-bold">{freeViewLimit}</strong>
                <span style={{ marginLeft: 4 }}>free preview</span>
              </>
            )}
          </span>
          <TierPill isFree={free} />
        </div>
      </div>

      {/* Footer — two equal-width small buttons sitting on cream. */}
      <div
        className="flex border-t border-sand bg-cream"
        style={{ gap: 6, padding: '10px 12px' }}
      >
        <Link
          to={`/card-decks/${deck.id}/cards`}
          className="no-underline"
          style={{ flex: 1 }}
        >
          <Button
            variant="default"
            trailingIcon={<span aria-hidden>→</span>}
            style={{ width: '100%', height: 32, fontSize: 12 }}
          >
            Manage Cards
          </Button>
        </Link>
        <Link
          to={`/card-decks/${deck.id}`}
          className="no-underline"
          style={{ flex: 1 }}
        >
          <Button
            variant="default"
            style={{ width: '100%', height: 32, fontSize: 12 }}
          >
            Edit
          </Button>
        </Link>
      </div>
    </div>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

const STATUS_FILTERS = ['all', 'active', 'inactive'];

export default function CardDecksMainPageV2() {
  const dispatch = useDispatch();
  const decks = useSelector((s) => s.card_decks.decks);

  const [localList, setLocalList] = useState([]);
  const [status, setStatus] = useState('all');

  useEffect(() => {
    dispatch(fetchAllDecks());
  }, [dispatch]);

  useEffect(() => {
    if (!Array.isArray(decks)) return;
    // The fetchAllDecks reducer already stores sorted decks; mirror
    // them locally so drag mutations can be optimistic.
    setLocalList(decks);
  }, [decks]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = localList.findIndex((d) => d.id === active.id);
      const newIndex = localList.findIndex((d) => d.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;

      const next = arrayMove(localList, oldIndex, newIndex);
      setLocalList(next);
      // sortCardDeck POSTs PUT /decks/sort-order with { decks: [ids] }
      dispatch(sortCardDeck(next));
    },
    [localList, dispatch],
  );

  const visible = useMemo(() => {
    if (status === 'all') return localList;
    return localList.filter((d) =>
      status === 'active' ? !!d.is_active : !d.is_active,
    );
  }, [localList, status]);

  const counts = useMemo(() => {
    return localList.reduce(
      (acc, d) => {
        acc.all += 1;
        if (d.is_active) acc.active += 1;
        else acc.inactive += 1;
        return acc;
      },
      { all: 0, active: 0, inactive: 0 },
    );
  }, [localList]);

  const ids = useMemo(() => visible.map((d) => d.id), [visible]);

  if (!Array.isArray(decks)) {
    return (
      <div className="bg-bg min-h-full">
        <PageHeader
          eyebrow="Content Library"
          title="Card Decks"
        />
        <div
          className="flex items-center justify-center text-text-muted"
          style={{ height: 256 }}
        >
          Loading card decks…
        </div>
      </div>
    );
  }

  const totalLabel = `${localList.length.toLocaleString()} items`;

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Content Library"
        title="Card Decks"
        total={totalLabel}
        actions={
          <Link to="/card-decks/create" className="no-underline">
            <Button variant="coral" leadingIcon={PlusSign}>
              New Deck
            </Button>
          </Link>
        }
      />

      <div
        className="flex items-center"
        style={{ padding: '0 28px 16px', gap: 12 }}
      >
        <SegmentedControl
          value={status}
          onChange={setStatus}
          options={STATUS_FILTERS.map((v) => ({
            value: v,
            label: v === 'all' ? 'All' : v === 'active' ? 'Active' : 'Inactive',
            count: counts[v],
          }))}
        />
      </div>

      {visible.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{
            borderRadius: 12,
            padding: '40px 16px',
            fontSize: 13,
            margin: '0 28px 32px',
          }}
        >
          {status === 'all'
            ? 'No card decks yet — create one to get started.'
            : `No ${status} card decks.`}
        </div>
      ) : (
        <div style={{ padding: '0 28px 32px' }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={ids} strategy={rectSortingStrategy}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                  gap: 18,
                  alignContent: 'start',
                }}
              >
                {visible.map((d) => (
                  <SortableDeckCard key={d.id} deck={d} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}
